import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col } from "antd";
import { Icon, AccordionItemV2, Font } from "pepsico-ds";
import SingleSelectDropdown from "../../../components/Dropdown/SingleSelectDropdown";
import ActualLegendRISI from "../../../assets/ActualLegendRISI.svg";
import Vector from "../../../assets/Vector.png";
import { exportRowsToExcel } from "../../../utils/excelExport";
import { useToast } from "../../../components/Toast/ToastContext";
import {
  FetchCMAPageFilterAction,
  FetchCMALineChartAction,
  FetchCMATableAction,
  FetchCMADateAction,
  FetchCmaDetailReportAction
} from "../../../redux/action/SBACCIAction";
import {
  changeFromDateOptionsUtil,
  changeToDateOptionsUtil,
  getGeographyByProducts,
  getDropdownOptions,
  getGradesByGeoAndProduct,
  getConceptsByGeoProductAndGrade,
  getTermsByGeoProductGradeAndConcept,
  getUnitsByGeoProductGradeConceptAndTerm
} from "../../KPIOverview/KpiUtils";
import { getOverlappingOptionsV2 } from "../../../utils/utils";
import { FetchSPGlobalPageLabelCountAction } from "../../../redux/action/SPGlobalAction";

// This context/provider owns ALL CMA state, redux selectors, handler
// functions and effects so that CMA logic is fully self-contained and can
// be shared between the two separate <CmaSource/> render sites in
// SBACCIScreen.js ("filters" and "content") without lifting state back up
// to the parent.
const CmaSourceContext = createContext(null);

export const useCmaSource = () => useContext(CmaSourceContext);

export const CmaSourceProvider = ({ children, isUserDataFetched, selectedTab, isCmaSelected }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const userData = useSelector(state => state.getUserInfoReducer);
  const cmaFilterData = useSelector(state => state.getCMAPageFilterReducer);
  const cmaLineChartData = useSelector(state => state.getCMALineChartReducer);
  const cmaTableData = useSelector(state => state.getCMATableReducer);
  const cmaDateData = useSelector(state => state.getCMADateReducer);
  const cmaDetailReportData = useSelector(state => state.getCmaDetailReportReducer);

  const isUserDataLoading = userData?.loading ?? true;
  const isFilterDataLoading = cmaFilterData?.loading ?? true;
  const isLineDataLoading = cmaLineChartData?.loading ?? true;
  const isTableDataLoading = cmaTableData?.loading ?? true;
  const activePricingLineChartData = cmaLineChartData;
  const activePricingTableData = cmaTableData;

  const [geographyList, setGeographyList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [gradeList, setGradeList] = useState([]);
  const [conceptList, setConceptList] = useState([]);
  const [termsList, setTermsList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [geographySelected, setGeographySelected] = useState("");
  const [productSelected, setProductSelected] = useState("");
  const [gradeSelected, setGradeSelected] = useState("");
  const [conceptSelected, setConceptSelected] = useState("");
  const [termsSelected, setTermsSelected] = useState("");
  const [unitsSelected, setUnitsSelected] = useState([]);

  const [cmaPricingFromDates, setCmaPricingFromDates] = useState([]);
  const [cmaPricingToDates, setCmaPricingToDates] = useState([]);
  const [cmaPricingFromDatesSelected, setCmaPricingFromDatesSelected] = useState([]);
  const [cmaPricingToDatesSelected, setCmaPricingToDatesSelected] = useState([]);
  const [entireCmaMonthList, setEntireCmaMonthList] = useState([]);

  const [cmaReportFromDates, setCmaReportFromDates] = useState([]);
  const [cmaReportToDates, setCmaReportToDates] = useState([]);
  const [cmaReportFromDateSelected, setCmaReportFromDateSelected] = useState([]);
  const [cmaReportToDateSelected, setCmaReportToDateSelected] = useState([]);
  const [cmaReportEntireMonthList, setCmaReportEntireMonthList] = useState([]);

  const [isCMADataLoading, setIsCMADataLoading] = useState(true);
  const [cmaPdfReportData, setCmaPdfReportData] = useState({});
  const [pdfUrl, setPdfUrl] = useState(null);
  const [selectedCmaAccordionKey, setSelectedCmaAccordionKey] = useState(null);

  const [resinChartData, setResinChartData] = useState([]);
  const [resinForecastStartVal, setResinForecastStartVal] = useState(null);
  const [resinSeriesLineChartIndex, setResinSeriesLineChartIndex] = useState([]);
  const [resinLineChartActualData, setResinLineChartActualData] = useState([]);
  const [resinFirstForecastYearMonth, setResinFirstForecastYearMonth] = useState(null);
  const [resinFirstForecastKeysToBeMergedActualLast, setResinFirstForecastKeysToBeMergedActualLast] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState("");

  const linechartRef = useRef();
  const cmaTableRef = useRef();

  const cmaReportSections = [
    { key: "weeklyReport", label: "Weekly" },
    { key: "monthlyReport", label: "Monthly" }
  ];
  const renderedCmaSections = cmaReportSections.filter(
    s => Array.isArray(cmaPdfReportData?.[s.key]) && cmaPdfReportData[s.key].length > 0
  );
  const cmaAccordionSelectedIndex = renderedCmaSections.findIndex(
    s => s.key === selectedCmaAccordionKey
  );

  const cmaColumns = [
    { columnKey: "a", header: "" },
    {
      cellData: ctx => <span>{ctx.row.original.month}</span>,
      columnAlignment: "left",
      size: 90, minSize: 90, maxSize: 140,
      header: "MONTH",
      columnKey: "month",
    },
    {
      
      cellData: ctx => {
        const val = ctx.row.original.price;
        return <span>{val === null ? "--" : val}</span>;
      },
      columnAlignment: "left",
      header: "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      size: 90, 
      minSize: 90, 
      maxSize: 140,
      columnKey: "price",
    },
    {
      columnAlignment: "left",
      cellData: ctx => {
        const val = ctx.row.original.momChange;
        if (val === null) return <span>--</span>;
        return (
          <span style={{ color: "green", display: "inline-flex", alignItems: "center", gap: 4 }}>
            {ctx.row.original.momChange < 0 ? (
              <span style={{ color: "#027C06", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon alt="alt value" icon={"arrow_downward"} size="small" style={{ color: "#027C06" }} />
                {val}
              </span>
            ) : (
              <span style={{ color: "#C12C01", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon alt="alt value" icon={"arrow_upward"} size="small" style={{ color: "#C12C01" }} />
                {val}
              </span>
            )}
          </span>
        );
      },
      size: 90, minSize: 90, maxSize: 140,
      header: "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      columnKey: "momChange",
    },
    {
      columnAlignment: "left",
      cellData: ctx => {
        const val = ctx.row.original.momChangePct;
        if (val === null) return <span>--</span>;
        return (
          <span style={{ color: "green", display: "inline-flex", alignItems: "center", gap: 4 }}>
            {ctx.row.original.momChangePct < 0 ? (
              <span style={{ color: "#027C06", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon alt="alt value" icon={"arrow_downward"} size="small" style={{ color: "#027C06" }} />
                {val}
              </span>
            ) : (
              <span style={{ color: "#C12C01", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon alt="alt value" icon={"arrow_upward"} size="small" style={{ color: "#C12C01" }} />
                {val}
              </span>
            )}
          </span>
        );
      },
      size: 90, minSize: 90, maxSize: 140,
      header: "MOM CHANGE %",
      columnKey: "momChangePct",
    }
  ];

  /* istanbul ignore next */
  const monthLabelToObject = label => {
    if (!label) {
      return null;
    }
    const d = new Date(`${label} 01`);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    const year = d.getFullYear();

    const month = d.getMonth() + 1;

    const paddedMonth = String(month).padStart(2, "0");

    return {
      value: `${year}-${paddedMonth}-01`,
      label,
      id: `${year}-${month}`,
      displayText: label,
    };
  };

  /* istanbul ignore next */
  const generateMonthObjectsFromLabels = (minLabel, maxLabel) => {
    const result = [];
    if (!minLabel || !maxLabel) {
      return result;
    }
    const end = new Date(`${maxLabel} 01`);
    const start = new Date(`${minLabel} 01`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return result;
    } 
    const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (monthsDiff < 0) {
      return result;
    }
    const formatter = 
      new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
    for (let i = 0; i <= monthsDiff; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      const displayText = formatter.format(d);
      const paddedMonth = String(month).padStart(2, "0");
      result.push({ 
        id: `${year}-${month}`,
        displayText, 
        label: displayText, 
        value: `${year}-${paddedMonth}-01` 
      });
    }
    return result;
  };

  // function ensureFourDigitYear(label) {
  //   if (!label) return label;
  //   return label.replace(/(\w{3,})\s(\d{2})$/, (m, month, year) => `${month} 20${year}`);
  // }
  function ensureFourDigitYear(label) {
    if (!label) return label;
    const trimmedLabel = label.trim();
    const lastSpaceIndex = trimmedLabel.lastIndexOf(" ");
    if (lastSpaceIndex === -1) return label;
    const month = trimmedLabel.slice(0, lastSpaceIndex);
    const year = trimmedLabel.slice(lastSpaceIndex + 1);
    if (year.length === 2 && /^\d{2}$/.test(year)) {
      return `${month} 20${year}`;
    }
    return label;
  }

  /* istanbul ignore next */
  const generateMonthObjects = (from, to) => {
    const result = [];
    const start = new Date(Number(from.slice(0, 4)), 
      Number(from.slice(4, 6)) - 1, 1);
    const end = new Date(Number(to.slice(0, 4)), 
      Number(to.slice(4, 6)) - 1, 1);
    const monthsDiff =
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (monthsDiff < 0) {
      return result;
    }
    const formatter = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
    for (let i = 0; i <= monthsDiff; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const month = d.getMonth() + 1;

      const year = d.getFullYear();

      const paddedMonth = String(month).padStart(2, "0");
      const displayText = formatter.format(d);
      result.push({ id: `${year}-${month}`, displayText, 
        label: displayText, value: `${year}-${paddedMonth}-01` });
    }

    return result;
  };

  const loadCMAData = () => {
    const cmaPricingRequestObject = {
      geography: JSON.parse(localStorage.getItem("cma-geo"))?.[0]?.displayText,
      product: JSON.parse(localStorage.getItem("cma-product"))?.[0]?.displayText,
      grade: JSON.parse(localStorage.getItem("cma-grades"))?.[0]?.displayText,
      concept: JSON.parse(localStorage.getItem("cma-concept"))?.[0]?.displayText,
      terms: JSON.parse(localStorage.getItem("cma-terms"))?.[0]?.displayText,
      unit: JSON.parse(localStorage.getItem("cma-units"))?.[0]?.displayText,
      fromDate: JSON.parse(localStorage.getItem("cma-fromDate"))?.[0]?.value || null,
      toDate: JSON.parse(localStorage.getItem("cma-toDate"))?.[0]?.value || null
    };

    dispatch(FetchCMALineChartAction(cmaPricingRequestObject));
    dispatch(FetchCMATableAction(cmaPricingRequestObject));
  };

  const initializePricingDateRanges = () => {
    const dateInfo = cmaFilterData?.date;
    if (!dateInfo) {
      return;
    }
    const { minYearMonth, maxYearMonth, 
      defaultMin, defaultMax } = dateInfo;
    const months = generateMonthObjectsFromLabels(ensureFourDigitYear(minYearMonth),ensureFourDigitYear(maxYearMonth)
    );
    if (!months.length) { 
      return;
    }

    setEntireCmaMonthList(months);
    const defaultTo = monthLabelToObject(ensureFourDigitYear(defaultMax)) || months[months.length - 1];
    const defaultFrom = monthLabelToObject(ensureFourDigitYear(defaultMin)) || months[0];

    setCmaPricingToDates(changeToDateOptionsUtil(defaultFrom, months));
    setCmaPricingFromDates(changeFromDateOptionsUtil(defaultTo, months));

    setCmaPricingToDatesSelected([defaultTo]);
    setCmaPricingFromDatesSelected([defaultFrom]);

    localStorage.setItem("cma-fromDate", JSON.stringify([defaultFrom]));
    localStorage.setItem("cma-toDate", JSON.stringify([defaultTo]));
  };

  /* istanbul ignore next */
  const processResinChartData = (chartData = activePricingLineChartData) => {
    const { actual = [], forecast = [], denomination = "$", series = [] } = chartData || {};
    if (!actual.length && !forecast.length) {
      return;
    }

    setResinLineChartActualData(actual);

    const firstThreeForecasts = forecast.slice(0, 3);
    const lastThreeActuals = actual.slice(-3);

    const forecastMap = new Map(firstThreeForecasts.map(item => [item.yearMonth, item]));

    const keysArray = Object.keys(firstThreeForecasts[0] || {})
    .filter
    (
      key => key !== "yearMonth" && key !== "assessmentDate"
    );
    setResinFirstForecastKeysToBeMergedActualLast(keysArray);

    let isFirstSet = false;
    let firstForecastYM = null;
    const mergedActuals = actual.map(act => 
    {
      const forecastMatch = forecastMap.get(act.yearMonth);
      if (forecastMatch) {

        if (!isFirstSet) {

          isFirstSet = true;
          firstForecastYM = forecastMatch.yearMonth;
        }

        return { ...act, ...forecastMatch, isForecastLabel: true };
      }

      return act;
    });

    setResinFirstForecastYearMonth(firstForecastYM);

    const mergedMonths = new Set(lastThreeActuals.map(a => a.yearMonth));

    const remainingForecasts = forecast.filter(f => !mergedMonths.has(f.yearMonth));

    const combined = [...mergedActuals,
       ...remainingForecasts];
    setResinChartData(combined);

    const forecastMonths = new Set(firstThreeForecasts.map(f => f.yearMonth));
    const firstMerged = lastThreeActuals.find(a => forecastMonths.has(a.yearMonth));
    setResinForecastStartVal(firstMerged?.yearMonth || forecast?.[0]?.yearMonth || null);

    setResinSeriesLineChartIndex(series);
    setCurrencySymbol(denomination);
  };

  const onGeographyChange = val => {
    if (val && val.length > 0) {
      const selectedGeo = val;
      setGeographySelected(val);
      localStorage.setItem("cma-geo", JSON.stringify(val));

      const data = cmaFilterData?.product;
      const selectedProduct = productSelected;

      const grades = getGradesByGeoAndProduct(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText);
      setGradeSelected([grades?.[0]]);
      setGradeList(grades);
      localStorage.setItem("cma-grades", JSON.stringify([grades?.[0]]));

      const concepts = getConceptsByGeoProductAndGrade(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText);
      setConceptSelected([concepts?.[0]]);
      setConceptList(concepts);
      localStorage.setItem("cma-concept", JSON.stringify([concepts?.[0]]));

      const terms = getTermsByGeoProductGradeAndConcept(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText, concepts?.[0]?.displayText);
      setTermsList(terms);
      localStorage.setItem("cma-terms", JSON.stringify([terms?.[0]]));
      setTermsSelected([terms?.[0]]);

      const units = getUnitsByGeoProductGradeConceptAndTerm(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText, concepts?.[0]?.displayText, terms?.[0]?.displayText);
      setUnitList(units);
      localStorage.setItem("cma-units", JSON.stringify([units?.[0]]));
      setUnitsSelected([units?.[0]]);

      loadCMAData();
    }
  };

  const onProductChange = val => {
    if (val && val.length > 0) {
      const selectedProduct = val;
      setProductSelected(selectedProduct);

      const data = cmaFilterData?.product;
      localStorage.setItem("cma-product", JSON.stringify(selectedProduct));

      const geographies = getGeographyByProducts(data, selectedProduct?.[0]?.displayText);
      setGeographyList(geographies);
      const preferredGeography = getOverlappingOptionsV2(geographies, [geographySelected?.[0]?.displayText]);
      const selectedGeo = preferredGeography?.length ? preferredGeography : geographies.slice(0, 1);
      setGeographySelected(selectedGeo);
      localStorage.setItem("cma-geo", JSON.stringify(selectedGeo));

      const grades = getGradesByGeoAndProduct(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText);
      setGradeList(grades);
      setGradeSelected([grades?.[0]]);
      localStorage.setItem("cma-grades", JSON.stringify([grades?.[0]]));

      const concepts = getConceptsByGeoProductAndGrade(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText);
      setConceptList(concepts);
      setConceptSelected([concepts?.[0]]);
      localStorage.setItem("cma-concept", JSON.stringify([concepts?.[0]]));

      const terms = getTermsByGeoProductGradeAndConcept(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText, concepts?.[0]?.displayText);
      setTermsList(terms);
      setTermsSelected([terms?.[0]]);
      localStorage.setItem("cma-terms", JSON.stringify([terms?.[0]]));

      const units = getUnitsByGeoProductGradeConceptAndTerm(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText, concepts?.[0]?.displayText, terms?.[0]?.displayText);
      setUnitList(units);
      setUnitsSelected([units?.[0]]);
      localStorage.setItem("cma-units", JSON.stringify([units?.[0]]));

      loadCMAData();
    }
  };

  const onGradeChange = val => {
    if (val && val.length > 0) {
      const data = cmaFilterData?.product;

      setGradeSelected(val);
      localStorage.setItem("cma-grades", JSON.stringify(val));

      const concepts = getConceptsByGeoProductAndGrade(data, geographySelected?.[0]?.displayText, productSelected?.[0]?.displayText, val?.[0]?.displayText);
      setConceptList(concepts);
      setConceptSelected([concepts?.[0]]);
      localStorage.setItem("cma-concept", JSON.stringify([concepts?.[0]]));

      const terms = getTermsByGeoProductGradeAndConcept(data, geographySelected?.[0]?.displayText, productSelected?.[0]?.displayText, val?.[0]?.displayText, concepts?.[0]?.displayText);
      setTermsList(terms);
      setTermsSelected([terms?.[0]]);
      localStorage.setItem("cma-terms", JSON.stringify([terms?.[0]]));

      const units = getUnitsByGeoProductGradeConceptAndTerm(data, geographySelected?.[0]?.displayText, productSelected?.[0]?.displayText, val?.[0]?.displayText, concepts?.[0]?.displayText, terms?.[0]?.displayText);
      setUnitList(units);
      setUnitsSelected([units?.[0]]);
      localStorage.setItem("cma-units", JSON.stringify([units?.[0]]));

      loadCMAData();
    }
  };

  const onConceptChange = val => {
    if (val && val.length > 0) {
      const data = cmaFilterData?.product;
      setConceptSelected(val);
      localStorage.setItem("cma-concept", JSON.stringify(val));

      const terms = getTermsByGeoProductGradeAndConcept(data, geographySelected?.[0]?.displayText, productSelected?.[0]?.displayText, gradeSelected?.[0]?.displayText, val?.[0]?.displayText);
      setTermsList(terms);
      setTermsSelected([terms?.[0]]);
      localStorage.setItem("cma-terms", JSON.stringify([terms?.[0]]));

      const units = getUnitsByGeoProductGradeConceptAndTerm(data, geographySelected?.[0]?.displayText, productSelected?.[0]?.displayText, gradeSelected?.[0]?.displayText, val?.[0]?.displayText, terms?.[0]?.displayText);
      setUnitList(units);
      setUnitsSelected([units?.[0]]);
      localStorage.setItem("cma-units", JSON.stringify([units?.[0]]));
      loadCMAData();
    }
  };

  const onTermsChange = val => {
    if (val && val.length > 0) {
      const data = cmaFilterData?.product;
      setTermsSelected(val);
      localStorage.setItem("cma-terms", JSON.stringify(val));

      const units = getUnitsByGeoProductGradeConceptAndTerm(data, geographySelected?.[0]?.displayText, productSelected?.[0]?.displayText, gradeSelected?.[0]?.displayText, conceptSelected?.[0]?.displayText, val?.[0]?.displayText);
      setUnitList(units);
      setUnitsSelected([units?.[0]]);
      localStorage.setItem("cma-units", JSON.stringify([units?.[0]]));
      loadCMAData();
    }
  };

  const onUnitsChange = val => {
    if (val && val.length > 0) {
      setUnitsSelected(val);
      localStorage.setItem("cma-units", JSON.stringify(val));
      loadCMAData();
    }
  };

  const changeCmaFromDateOptions = toDateMonthSelected => {
    const filteredMonths = changeFromDateOptionsUtil(toDateMonthSelected, entireCmaMonthList);
    setCmaPricingFromDates(filteredMonths);
  };

  const changeCmaToDateOptions = fromDateMonthSelected => {
    const filteredMonths = changeToDateOptionsUtil(fromDateMonthSelected, entireCmaMonthList);
    setCmaPricingToDates(filteredMonths);
  };

  const changeCmaReportsFromDateOptions = toDateMonthSelected => {
    const filteredMonths = changeFromDateOptionsUtil(toDateMonthSelected, cmaReportEntireMonthList);
    setCmaReportFromDates(filteredMonths);
  };

  const changeCmaReportsToDateOptions = fromDateMonthSelected => {
    const filteredMonths = changeToDateOptionsUtil(fromDateMonthSelected, cmaReportEntireMonthList);
    setCmaReportToDates(filteredMonths);
  };

  const onPETResinFromDateChange = val => {
    if (val && val.length > 0) {
      setCmaPricingFromDatesSelected(val);
      localStorage.setItem("cma-fromDate", JSON.stringify(val));
      changeCmaToDateOptions(val[0]);
      loadCMAData();
    }
  };

  const onPETResinToDateChange = val => {
    if (val && val.length > 0) {
      setCmaPricingToDatesSelected(val);
      localStorage.setItem("cma-toDate", JSON.stringify(val));
      changeCmaFromDateOptions(val[0]);

      loadCMAData();
    }
  };

  const onCmaReportFromDateChange = val => {
    if (val && val.length > 0) {
      setCmaReportFromDateSelected(val);
      changeCmaReportsToDateOptions(val[0]);
      const requestObject = {
        fromDate: val[0]?.value,
        toDate: cmaReportToDateSelected?.length > 0 ? cmaReportToDateSelected[0]?.value : null
      };
      setIsCMADataLoading(true);
      dispatch(FetchCmaDetailReportAction(requestObject));
      setPdfUrl(null);
    }
  };

  const onCmaReportToDateChange = val => {
    if (val && val.length > 0) {
      setCmaReportToDateSelected(val);
      changeCmaReportsFromDateOptions(val[0]);
      const requestObject = {
        fromDate: cmaReportFromDateSelected?.length > 0 ? cmaReportFromDateSelected[0]?.value : null,
        toDate: val[0]?.value
      };
      setIsCMADataLoading(true);
      dispatch(FetchCmaDetailReportAction(requestObject));
      setPdfUrl(null);
    }
  };

  const resetButtonHandler = () => {
    dispatch(FetchCMAPageFilterAction());
  };

  /* istanbul ignore next */
  const downloadExcel = async () => {
    const columnsToInclude = ["MONTH", 
      "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")", 
      "MOM CHANGE %"];

    const columnMapping = {
      month: "MONTH", price: "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      momChange: "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")", momChangePct: "MOM CHANGE %"
    };

    const transformedData = 
    activePricingTableData?.ProductPriceDetails?.map(item => {
      const newItem = {};
      columnsToInclude
      .forEach(col => {
        const originalKey = Object.keys(columnMapping).find(key => columnMapping[key] === col);
        newItem[col] = item[originalKey];
      });
      return newItem;
    });

    await exportRowsToExcel({
      rows: transformedData,
      headers: columnsToInclude,
      fileName: "Product Price Details.xlsx",
    });
  };

  /* istanbul ignore next */
  const checkPDFDownloaded = (isPdfDownloaded, isModal, fileURL, isExcel, isTable, isJpeg) => {
    if (isPdfDownloaded) {
      if (isModal) {
        showToast("success", !isJpeg ? "PDF of full page successfully downloaded." : "JPEG of full page successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else if (isTable) {
        showToast("success", !isJpeg ? "PDF of a table successfully downloaded." : "JPEG of a table successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else {
        showToast("success", !isJpeg ? "PDF of a chart successfully downloaded." : "JPEG of a chart successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      }
    } else if (isExcel) {
      downloadExcel();
    } else {
      showToast("Error", "Failed to download. Please try again.", {
        icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
      });
    }
  };

  const filterLabelStyle = {
    fontFamily: "Inter",
    fontSize: "0.75rem",
    fontWeight: "500",
    marginBottom: "3px"
  };

  const renderPricingFilterDropdown = (label, childList, selectedValue, setSelectedValue, colProps = {}) => (
    <Col {...colProps}>
      <div style={filterLabelStyle}>{label}</div>
      <SingleSelectDropdown
        className="buy-con-dropdown-modal cma-dropdown"
        childList={childList}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
      />
    </Col>
  );

  /* istanbul ignore next */
  const makeLabel = (text, count) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontWeight: "bold" }}>{text}</span>
      <span style={{ color: "#0662C4" }}>({count})</span>
    </span>
  );

  /* istanbul ignore next */
  const renderSection = (items, label) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
      <AccordionItemV2
        key={label}
        label={makeLabel(label, items.length)}
        onUpdate={function wRe() {}}
        presentation="box-shadow"
        className="custom-accordion-items"
      >
        {items.map((item, idx) => (
          <div key={`${label}-${idx}`}>
            <Font
              textColorClass="$color-text-secondary"
              onClick={() => {
                setPdfUrl(item.url);
              }}
              style={{
                color: item.url === pdfUrl ? "#004895" : "#2D2D2D",
                fontWeight: "bold",
                cursor: "pointer"
              }}
              aria-label="sbacci-pdf-link"
              data-fs="sbacci-pdf-link"
            >
              {item.reportName}
            </Font>
            {idx < items.length - 1 && (
              <hr style={{ border: 0, borderTop: "1px solid #D8E7FB", margin: "4px 0" }} />
            )}
          </div>
        ))}
      </AccordionItemV2>
    );
  };

  /* istanbul ignore next */
  const renderForecastLegend = () => (
    <div>
      <img src={ActualLegendRISI} alt="img" style={{ marginRight: "1rem" }} />
      <span style={{ marginRight: "0.25rem", position: "relative", top: "3px" }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <line x1="0" y1="6" x2="12" y2="6" stroke={"#616161"} strokeWidth="2" strokeLinecap="round" strokeDasharray="5 0" />
        </svg>
      </span>
      <span style={{ marginRight: "0.25rem", position: "relative", top: "3px" }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <line x1="0" y1="6" x2="18" y2="6" stroke={"#616161"} strokeWidth="2" strokeLinecap="round" strokeDasharray="5 0" />
        </svg>
      </span>
      <span style={{ color: "#616161", position: "relative", top: "2px" }}>Forecast</span>
    </div>
  );

  // Fetch CMA filter data whenever CMA becomes the active source (covers
  // both the initial default-CMA page load and switching back to CMA from
  // another source) once user info has been fetched. isCmaSelected is
  // included so this refires on every switch back to CMA even though this
  // provider itself stays mounted for the lifetime of SBACCIScreen.
  useEffect(() => {
    if (isUserDataFetched && isCmaSelected) {
      setIsCMADataLoading(true);
      setPdfUrl(null);
      const location = window.location.pathname;
      dispatch(FetchSPGlobalPageLabelCountAction(location));
      dispatch(FetchCMAPageFilterAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserDataFetched, isCmaSelected]);

  useEffect(() => {
    if (isUserDataFetched && cmaFilterData && cmaFilterData.type === "SUCCESS_CMA_PAGE_FILTER_ACTION") {
      const data = cmaFilterData?.product;
      const allProducts = getDropdownOptions([...new Set((data || []).map(product => product.name))]);
      setProductList(allProducts);
      const preferredProduct = getOverlappingOptionsV2(allProducts, [cmaFilterData?.cmaDefault?.defaultProduct]);
      const selectedProduct = preferredProduct?.length ? preferredProduct : allProducts.slice(0, 1);
      setProductSelected(selectedProduct);
      localStorage.setItem("cma-product", JSON.stringify(selectedProduct));

      const geoList = getGeographyByProducts(data, selectedProduct?.[0]?.displayText);
      setGeographyList(geoList);
      const preferredGeography = getOverlappingOptionsV2(geoList, [cmaFilterData?.cmaDefault?.defaultGeography]);
      const selectedGeo = preferredGeography?.length ? preferredGeography : geoList.slice(0, 1);
      setGeographySelected(selectedGeo);
      localStorage.setItem("cma-geo", JSON.stringify(selectedGeo));

      const grades = getGradesByGeoAndProduct(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText);
      setGradeList(grades);
      setGradeSelected([grades?.[0]]);
      localStorage.setItem("cma-grades", JSON.stringify([grades?.[0]]));

      const concepts = getConceptsByGeoProductAndGrade(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText);
      setConceptList(concepts);
      setConceptSelected([concepts?.[0]]);
      localStorage.setItem("cma-concept", JSON.stringify([concepts?.[0]]));

      const terms = getTermsByGeoProductGradeAndConcept(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText, concepts?.[0]?.displayText);
      setTermsList(terms);
      setTermsSelected([terms?.[0]]);
      localStorage.setItem("cma-terms", JSON.stringify([terms?.[0]]));

      const units = getUnitsByGeoProductGradeConceptAndTerm(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, grades?.[0]?.displayText, concepts?.[0]?.displayText, terms?.[0]?.displayText);
      setUnitList(units);
      setUnitsSelected([units?.[0]]);
      localStorage.setItem("cma-units", JSON.stringify([units?.[0]]));

      initializePricingDateRanges();
      loadCMAData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmaFilterData, isUserDataFetched]);

  useEffect(() => {
    if (isCmaSelected && selectedTab === 2) {
      dispatch(FetchCMADateAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, isCmaSelected]);

  useEffect(() => {
    if (cmaDateData && cmaDateData.type === "SUCCESS_CMA_DATE_ACTION") {
      const months = generateMonthObjects(cmaDateData?.fromDate, cmaDateData?.maxDate);

      setCmaReportEntireMonthList(months);
      setCmaReportFromDates(months);
      setCmaReportToDates(months);
      const formatMonthData = dateStr => {
        if (!dateStr || dateStr.length < 6) return null;
        const year = dateStr.slice(0, 4);
        const month = dateStr.slice(4, 6);
        const dateObj = new Date(`${year}-${month}-01`);
        const displayText = dateObj.toLocaleString("en-US", { month: "short", year: "numeric" });
        return { displayText, childContents: [], id: `${year}-${parseInt(month, 10)}`, value: `${year}-${month}-01` };
      };
      const fromDateVal = [formatMonthData(cmaDateData?.fromDate)];
      const toDateVal = [formatMonthData(cmaDateData?.maxDate)];
      setCmaReportFromDateSelected(fromDateVal);
      setCmaReportToDateSelected(toDateVal);

      const requestObject = {
        fromDate: `${cmaDateData.fromDate?.slice(0, 4)}-${cmaDateData.fromDate?.slice(4, 6)}-01`,
        toDate: `${cmaDateData.maxDate?.slice(0, 4)}-${cmaDateData.maxDate?.slice(4, 6)}-01`
      };

      dispatch(FetchCmaDetailReportAction(requestObject));
    }
  }, [cmaDateData]);

  useEffect(() => {
    if (isUserDataFetched && cmaDetailReportData && cmaDetailReportData.type === "SUCCESS_CMA_DETAIL_REPORT_ACTION") {
      setCmaPdfReportData(cmaDetailReportData);
      setIsCMADataLoading(false);
    }
  }, [cmaDetailReportData, isUserDataFetched]);

  useEffect(() => {
    if (selectedTab !== 2) return;

    const cmaSectionKeys = renderedCmaSections.map(s => s.key);
    const defaultId = cmaPdfReportData?.defaultId;
    let selectedKey = null;
    let selectedUrl = null;

    if (defaultId) {
      for (const key of cmaSectionKeys) {
        const items = cmaPdfReportData?.[key];
        if (Array.isArray(items)) {
          const matchedReport = items.find(report => report.reportId === defaultId);
          if (matchedReport) {
            selectedKey = key;
            selectedUrl = matchedReport.url;
            break;
          }
        }
      }
    }

    if (!selectedKey) {
      for (const key of cmaSectionKeys) {
        const items = cmaPdfReportData?.[key];
        if (Array.isArray(items) && items.length > 0) {
          selectedKey = key;
          selectedUrl = items?.[0]?.url || null;
          break;
        }
      }
    }

    setSelectedCmaAccordionKey(selectedKey);
    setPdfUrl(selectedUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, cmaPdfReportData]);

  useEffect(() => {
    processResinChartData(cmaLineChartData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmaLineChartData]);

  const value = {
    selectedTab,
    geographyList, 
    geographySelected, 
    onGeographyChange,
    productList, 
    productSelected, 
    onProductChange,
    gradeList, 
    gradeSelected, 
    onGradeChange,
    conceptList, 
    conceptSelected, 
    onConceptChange,
    termsList, 
    termsSelected, 
    onTermsChange,
    cmaPricingFromDates, 
    cmaPricingFromDatesSelected, 
    onPETResinFromDateChange,
    cmaPricingToDates, 
    cmaPricingToDatesSelected, 
    onPETResinToDateChange,
    unitList, 
    unitsSelected, 
    onUnitsChange,
    resetButtonHandler,
    renderPricingFilterDropdown,
    cmaReportFromDates, 
    cmaReportFromDateSelected, 
    onCmaReportFromDateChange,
    cmaReportToDates, 
    cmaReportToDateSelected, 
    onCmaReportToDateChange,
    isCMADataLoading,
    renderedCmaSections, 
    cmaAccordionSelectedIndex, 
    cmaPdfReportData,
    renderSection,
    pdfUrl,
    isUserDataLoading, 
    isFilterDataLoading, 
    isLineDataLoading,
    linechartRef,
    checkPDFDownloaded,
    renderForecastLegend,
    activePricingLineChartData, resinChartData, resinLineChartActualData, resinForecastStartVal, resinSeriesLineChartIndex, resinFirstForecastYearMonth,
    resinFirstForecastKeysToBeMergedActualLast,
    currencySymbol,
    cmaTableRef,
    isTableDataLoading,
    activePricingTableData,
    cmaColumns
  };

  return <CmaSourceContext.Provider value={value}>{children}</CmaSourceContext.Provider>;
};
